import {
  ButtonType,
  Icon,
  IconColor,
  IconName,
  LinkButton,
  Logo,
  SpinnerButton,
  TextSize,
  TextWeight,
  textStyleUtils
} from '@focaldata/cin-ui-components'
import Grid from '@mui/material/Grid'
import React, { useState } from 'react'
import styles from '../Auth.module.css'
import useStyles from '../Auth.styles'

export interface Props {
  onLogin: (email: string, password: string) => Promise<boolean | Error>
  onForgotPassword?: () => void
  onGoToRegister?: () => void
}

interface State {
  error: Error | undefined
  email: string
  password: string
  showPassword: boolean
}

enum FailStatus {
  unauthorized = '401',
  notFound = '404'
}

const Login: React.FC<Props> = (props: Props) => {
  const { onLogin, onGoToRegister, onForgotPassword }: Props = props
  const [loading, onLoading] = useState<boolean>(false)
  const [state, setState] = useState<State>({
    error: undefined,
    email: '',
    password: '',
    showPassword: false
  })

  const handleLogin: (event: React.SyntheticEvent) => void = async (event) => {
    event.preventDefault()
    onLoading(true)
    const loginResponse = await onLogin(
      // @todo Legacy eslint violation – fix this when editing
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      state.email?.toLowerCase(),
      state.password
    )

    if (loginResponse) {
      onLoading(false)
    }
    if (loginResponse instanceof Error) {
      setState({ ...state, error: loginResponse })
    }
  }

  const renderErrorMessage = (errorState: Error) => {
    if (
      errorState.message === FailStatus.unauthorized ||
      errorState.message === FailStatus.notFound
    ) {
      return 'Your username or password are incorrect. Please try again.'
    }

    return "There's an issue connecting to the server. Please try again."
  }

  const { classes } = useStyles()
  const { classes: textClasses, cx: classNames } =
    textStyleUtils.useTextStyles()

  const handleChange =
    (prop: keyof State) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setState({ ...state, [prop]: event.target.value })
    }

  return (
    <Grid className={styles.page} container justifyContent="center">
      <Grid className={classes.maxWidth}>
        <form onSubmit={handleLogin}>
          <Grid
            container
            justifyContent="center"
            direction="column"
            alignItems="center"
          >
            <Logo width={42} type="icon" />
            <h1 className={styles.title}>Log in to your account</h1>
            <p className={styles.subtitle}>
              Welcome back. Please enter your details.
            </p>
            <Grid
              item
              container
              direction="column"
              alignItems="flex-start"
              className={classes.gridItem}
            >
              <p className={styles['input-label']}>Email</p>
              <input
                value={state.email}
                onChange={handleChange('email')}
                aria-label="email"
                placeholder="Enter your email"
                className={styles.input}
              />
            </Grid>
            <Grid item container className={classes.gridItem}>
              <p className={styles['input-label']}>Password</p>
              <input
                type="password"
                value={state.password}
                onChange={handleChange('password')}
                aria-label="password"
                className={styles.input}
                placeholder="Enter your password"
              />
            </Grid>
            <Grid item container className={classes.gridItem}>
              <SpinnerButton
                onClick={handleLogin}
                className={classes.button}
                loading={loading}
                fullWidth
                buttonType={ButtonType.Submit}
              >
                Log in
              </SpinnerButton>
            </Grid>
            {state.error && (
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                className={classes.gridItem}
              >
                <Icon
                  name={IconName.InfoOutlinedIcon}
                  iconColor={IconColor.Error}
                />
                <p
                  className={classNames(
                    textClasses.default,
                    textClasses.sizeS,
                    textClasses.highlightError
                  )}
                >
                  {renderErrorMessage(state.error)}
                </p>
              </Grid>
            )}
            <Grid
              container
              justifyContent="center"
              className={classes.helperText}
            >
              <p className={classNames(textClasses.default)}>
                Don&apos;t have an account?
              </p>
              <LinkButton
                size={TextSize.m}
                weight={TextWeight.Bold}
                onClick={onGoToRegister}
                noPadding
                underlined
              >
                Sign up
              </LinkButton>
            </Grid>
            <Grid container justifyContent="center">
              <LinkButton
                size={TextSize.m}
                weight={TextWeight.Bold}
                onClick={onForgotPassword}
                noPadding
                underlined
              >
                Forgot your password?
              </LinkButton>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  )
}

export default Login
